'use client';

import { c as _c } from "react/compiler-runtime";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React, { Suspense } from 'react';
import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.tsx';
import { AdditionalSettingsArticleList, BlockProps, ComponentBaseProps } from '@sola/schema/lib/schema.ts';
import { extractArgsSearchResult } from '@sola/skippy-search-sdk/lib/logic.ts';
import { useListArticlesQuery } from '@sola/skippy-search-sdk/lib/queries.ts';
import { compactObject } from '@sola/utils/object.ts';
import { FeaturedArticleCard } from '../../../molecules/feature-article-card/feature-article-card.tsx';
import { useArticleQueryParams } from '../../../utils/query-params.tsx';
const queryClient = new QueryClient();
export type ArticleListProfoundProps = BlockProps & ComponentBaseProps;
const ArticleListProfoundSkeleton = () => {
  const $ = _c(9);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="FeaturedArticleCard__media"><Skeleton width="100%" height="100%" className="FeaturedArticleCard__image" borderRadius="var(--outline-radius-lg)" /><div className="FeaturedArticleCard__media-overlay" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="mb-comfy-md"><Skeleton height={20} width="90%" className="mb-snug-xs" /><Skeleton height={20} width="75%" /></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="max-w-md @lg:pr-roomy-lg">{t1}<div className="flex flex-col gap-snug-xs"><Skeleton height={14} width="100%" /><Skeleton height={14} width="95%" /><Skeleton height={14} width="88%" /><Skeleton height={14} width="82%" /></div></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Skeleton height={14} width={70} />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="flex flex-col gap-snug-lg">{t3}<div className="flex flex-row items-center gap-snup-lg"><div className="pr-comfy-md"><Skeleton height={32} width={32} borderRadius="50%" /></div><Skeleton height={14} width={100} /></div></div>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Skeleton height={14} width={90} />;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="flex flex-col gap-snug-lg">{t5}<div className="mt-snug-lg"><Skeleton height={14} width={120} /></div></div>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Skeleton height={14} width={50} />;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <section className="component"><div className="container"><div className="FeaturedArticleCard w-full max-w-xl mx-auto aspect-ratio-1 @md:aspect-ratio-16-9 @lg:aspect-ratio-21-9">{t0}<div className="FeaturedArticleCard__content">{t2}<div className="FeaturedArticleCard__meta flex items-start justify-between gap-comfy-xl flex-wrap pt-comfy-lg">{t4}{t6}<div className="flex flex-col gap-snug-lg sm:ml-auto text-left">{t7}<div className="flex flex-wrap justify-end gap-snug-xl mt-snug-lg"><Skeleton height={20} width={75} borderRadius="9999px" /><Skeleton height={20} width={85} borderRadius="9999px" /><Skeleton height={20} width={65} borderRadius="9999px" /></div></div></div></div></div></div></section>;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  return t8;
};
export const ArticleListProfound: React.FC<ArticleListProfoundProps> = t0 => {
  const $ = _c(16);
  const {
    seoLevel: t1,
    settings
  } = t0;
  const seoLevel = t1 === undefined ? 3 : t1;
  const {
    articleFilters
  } = useArticleQueryParams();
  let articleListSettings;
  let t2;
  let t3;
  let t4;
  if ($[0] !== articleFilters || $[1] !== settings) {
    articleListSettings = settings ? AdditionalSettingsArticleList.parse(settings) : undefined;
    const allFilters = {
      "taxonomy.articleTopics.keyword": articleListSettings?.topic || articleFilters.topic,
      "taxonomy.articleThemes.keyword": articleListSettings?.theme || articleFilters.theme,
      "taxonomy.articleCountry.keyword": articleListSettings?.country || articleFilters.country
    };
    const compactFilters = compactObject(allFilters);
    t4 = useListArticlesQuery;
    t2 = JSON.stringify({
      "taxonomy.other.keyword": "promotedContent",
      "taxonomy.articleType.keyword": articleListSettings?.articleType || undefined
    });
    t3 = JSON.stringify(compactFilters);
    $[0] = articleFilters;
    $[1] = settings;
    $[2] = articleListSettings;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    articleListSettings = $[2];
    t2 = $[3];
    t3 = $[4];
    t4 = $[5];
  }
  const t5 = articleListSettings?.limit || 1;
  let t6;
  if ($[6] !== t2 || $[7] !== t3 || $[8] !== t5) {
    t6 = {
      must: t2,
      filters: t3,
      perPage: t5,
      sort: "datePublished",
      sortDir: "desc"
    };
    $[6] = t2;
    $[7] = t3;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const {
    data,
    isLoading
  } = t4(t6);
  if (isLoading) {
    let t7;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <ArticleListProfoundSkeleton />;
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    return t7;
  }
  const article = data?.items?.[0];
  if (!article) {
    return null;
  }
  let t7;
  if ($[11] !== article) {
    t7 = extractArgsSearchResult(article);
    $[11] = article;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const cardProps = t7;
  let t8;
  if ($[13] !== cardProps || $[14] !== seoLevel) {
    t8 = <section className="component component--no-header"><div className="container"><FeaturedArticleCard {...cardProps} seoLevel={seoLevel} /></div></section>;
    $[13] = cardProps;
    $[14] = seoLevel;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
};
export const ArticleListProfoundProvider: React.FC<BlockProps> = props => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <ArticleListProfoundSkeleton />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <Suspense fallback={t0}><QueryClientProvider client={queryClient}><ArticleListProfound {...props} /></QueryClientProvider></Suspense>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};